'use client';

import React, { useState, useCallback, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';

const MODAL_QUERY_KEY = 'modal';

interface ModalTriggerProps {
  label: string;
  content: React.ReactNode | ((close: () => void) => React.ReactNode);
  children: React.ReactElement;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  onBack?: () => void;
  urlKey?: string;
  urlData?: Record<string, string>;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

function ModalTriggerInner({
  label,
  content,
  children,
  maxWidth = 'md',
  onBack,
  urlKey,
  urlData,
  disabled,
  onOpen,
  onClose: onCloseCallback,
}: ModalTriggerProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [localOpen, setLocalOpen] = useState(false);
  const isUrlOpen = urlKey ? searchParams.get(MODAL_QUERY_KEY) === urlKey : false;
  const isOpen = urlKey ? isUrlOpen : localOpen;

  const open = useCallback(() => {
    if (disabled) return;
    if (urlKey) {
      const params = new URLSearchParams(searchParams.toString());
      params.set(MODAL_QUERY_KEY, urlKey);
      // Add any additional data
      if (urlData) {
        Object.entries(urlData).forEach(([key, value]) => {
          params.set(`${MODAL_QUERY_KEY}_${key}`, value);
        });
      }
      window.history.pushState(null, '', `${pathname}?${params.toString()}`);
    } else {
      setLocalOpen(true);
    }
    onOpen?.();
  }, [disabled, urlKey, urlData, pathname, searchParams, onOpen]);

  const close = useCallback(() => {
    if (urlKey) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(MODAL_QUERY_KEY);
      // Remove all modal data params
      const keysToDelete: string[] = [];
      params.forEach((_, key) => {
        if (key.startsWith(`${MODAL_QUERY_KEY}_`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => params.delete(key));
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;
      window.history.pushState(null, '', newUrl);
    } else {
      setLocalOpen(false);
    }
    onCloseCallback?.();
  }, [urlKey, pathname, searchParams, onCloseCallback]);

  // Resolve content - support both ReactNode and render function
  const modalContent = typeof content === 'function' ? content(close) : content;

  return (
    <>
      {/* Wrap children in a clickable container that works for both server and client components */}
      <span
        onClick={disabled ? undefined : open}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          display: 'contents',
        }}
      >
        {children}
      </span>
      <ResponsiveModal
        open={isOpen}
        onClose={close}
        {...(onBack !== undefined && { onBack })}
        label={label}
        maxWidth={maxWidth}
      >
        {modalContent}
      </ResponsiveModal>
    </>
  );
}

export function ModalTrigger(props: ModalTriggerProps) {
  return (
    <Suspense fallback={props.children}>
      <ModalTriggerInner {...props} />
    </Suspense>
  );
}

export default ModalTrigger;
